import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'pyzone-gamification';

const defaultState = {
  enabled: true,
  xp: 0,
  level: 1,
  streak: 0,
  lastActivity: null,
  achievements: [],
  completedChallenges: [],
};

// XP thresholds for levels
const levelThresholds = [
  0,      // Level 1
  100,    // Level 2
  250,    // Level 3
  500,    // Level 4
  850,    // Level 5
  1300,   // Level 6
  1850,   // Level 7
  2500,   // Level 8
  3250,   // Level 9
  4100,   // Level 10
];

// Achievement definitions
const achievementDefinitions = {
  first_run: {
    id: 'first_run',
    name: 'צעד ראשון',
    nameEn: 'First Step',
    description: 'הרצת קוד בפעם הראשונה',
    icon: '🚀',
    xp: 10,
  },
  hello_world: {
    id: 'hello_world',
    name: 'שלום עולם!',
    nameEn: 'Hello World',
    description: 'כתבת את התוכנית הראשונה שלך',
    icon: '👋',
    xp: 15,
  },
  loop_master: {
    id: 'loop_master',
    name: 'שליט הלולאות',
    nameEn: 'Loop Master',
    description: 'השתמשת בלולאה בהצלחה',
    icon: '🔄',
    xp: 25,
  },
  bug_hunter: {
    id: 'bug_hunter',
    name: 'צייד באגים',
    nameEn: 'Bug Hunter',
    description: 'תיקנת שגיאה בקוד',
    icon: '🐛',
    xp: 20,
  },
  streak_3: {
    id: 'streak_3',
    name: 'רצף של 3',
    nameEn: '3 Day Streak',
    description: 'תרגלת 3 ימים ברציפות',
    icon: '🔥',
    xp: 30,
  },
  streak_7: {
    id: 'streak_7',
    name: 'רצף של שבוע',
    nameEn: 'Week Streak',
    description: 'תרגלת שבוע שלם ברציפות',
    icon: '⚡',
    xp: 100,
  },
  level_5: {
    id: 'level_5',
    name: 'מתכנת מתקדם',
    nameEn: 'Advanced Coder',
    description: 'הגעת לרמה 5',
    icon: '⭐',
    xp: 50,
  },
  chat_friend: {
    id: 'chat_friend',
    name: 'חבר של החונך',
    nameEn: 'Mentor\'s Friend',
    description: 'שאלת 5 שאלות את החונך',
    icon: '💬',
    xp: 20,
  },
};

export const useGamification = () => {
  const [state, setState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          return { ...defaultState, ...JSON.parse(saved) };
        } catch (e) {
          return defaultState;
        }
      }
    }
    return defaultState;
  });

  const [recentXP, setRecentXP] = useState(null);
  const [newAchievement, setNewAchievement] = useState(null);

  // Save state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Calculate level from XP
  const calculateLevel = useCallback((xp) => {
    for (let i = levelThresholds.length - 1; i >= 0; i--) {
      if (xp >= levelThresholds[i]) {
        return i + 1;
      }
    }
    return 1;
  }, []);

  // Get XP progress to next level
  const getLevelProgress = useCallback(() => {
    const currentLevelXP = levelThresholds[state.level - 1] || 0;
    const nextLevelXP = levelThresholds[state.level] || levelThresholds[levelThresholds.length - 1];
    const progress = ((state.xp - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;
    return Math.min(100, Math.max(0, progress));
  }, [state.xp, state.level]);

  // Add XP
  const addXP = useCallback((amount, reason = '') => {
    if (!state.enabled) return;
    
    setState(prev => {
      const newXP = prev.xp + amount;
      const newLevel = calculateLevel(newXP);
      
      return {
        ...prev,
        xp: newXP,
        level: newLevel,
        lastActivity: new Date().toISOString(),
      };
    });

    // Show XP popup
    setRecentXP({ amount, reason });
    setTimeout(() => setRecentXP(null), 2000);
  }, [state.enabled, calculateLevel]);

  // Unlock achievement
  const unlockAchievement = useCallback((achievementId) => {
    if (!state.enabled) return;
    
    const achievement = achievementDefinitions[achievementId];
    if (!achievement || state.achievements.includes(achievementId)) return;

    setState(prev => ({
      ...prev,
      achievements: [...prev.achievements, achievementId],
      xp: prev.xp + achievement.xp,
      level: calculateLevel(prev.xp + achievement.xp),
    }));

    // Show achievement popup
    setNewAchievement(achievement);
    setTimeout(() => setNewAchievement(null), 3000);
  }, [state.enabled, state.achievements, calculateLevel]);

  // Update streak
  const updateStreak = useCallback(() => {
    if (!state.enabled) return;
    
    const today = new Date().toDateString();
    const lastDate = state.lastActivity ? new Date(state.lastActivity).toDateString() : null;
    
    if (lastDate === today) return; // Already counted today
    
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isConsecutive = lastDate === yesterday.toDateString();
    
    setState(prev => ({
      ...prev,
      streak: isConsecutive ? prev.streak + 1 : 1,
      lastActivity: new Date().toISOString(),
    }));

    // Check streak achievements
    const newStreak = isConsecutive ? state.streak + 1 : 1;
    if (newStreak >= 3 && !state.achievements.includes('streak_3')) {
      unlockAchievement('streak_3');
    }
    if (newStreak >= 7 && !state.achievements.includes('streak_7')) {
      unlockAchievement('streak_7');
    }
  }, [state.enabled, state.lastActivity, state.streak, state.achievements, unlockAchievement]);

  // Toggle gamification
  const toggleGamification = useCallback(() => {
    setState(prev => ({ ...prev, enabled: !prev.enabled }));
  }, []);

  // Reset progress
  const resetProgress = useCallback(() => {
    setState(defaultState);
  }, []);

  return {
    ...state,
    recentXP,
    newAchievement,
    levelProgress: getLevelProgress(),
    addXP,
    unlockAchievement,
    updateStreak,
    toggleGamification,
    resetProgress,
    achievementDefinitions,
    levelThresholds,
  };
};
